<template>
  <log-quick-submit
    :model-value="activeTextType?.value ?? ''"
    :placeholder="placeholder"
    :return-placeholder="$t('ACTIVITY.CONTACTS.TO_LOG_A_CALL')"
    :loading="loading"
    :text-required="false"
    :is-modal="isModal"
    focus-on-mount
    @update:model-value="updateActiveTypeValue"
    @submit="onSubmit"
  >
    <template #tabs>
      <div v-if="callLog?.data?.id" class="m-4 flex">
        <lf-tab
          v-for="textType in textTypes"
          :key="textType.name"
          :data-cy="`tab-${textType.name}`"
          :active="activeTextType === textType"
          @click="handleActiveTextTypeChange(textType)"
        >
          <div
            class="text-headline"
            :class="{ 'font-bold': activeTextType === textType }"
          >
            {{ textType.name }}
          </div>
        </lf-tab>
      </div>
    </template>
    <template #supplemental>
      <slot name="supplemental" />
      <div
        v-if="isModal"
        class="flex items-center justify-end bg-grey-hover px-3 pt-1 text-xs"
      >
        <div class="flex items-center space-x-2">
          <span class="text-label">
            {{ $t("ACTIVITY.CALLS.UPLOAD_RECORDING") }}
          </span>
          <lf-switch v-model="uploadAfterSave" name="upload_recording" />
        </div>
      </div>
    </template>
    <template #actionBar>
      <div class="flex items-center space-x-2">
        <notes-dropdown
          v-model="callDisposition"
          :options="callStatuses"
          :colors-for-options="COLORS_BY_DISPOSITION_NOTE_STATUS"
          :placeholder="$t('ACTIVITY.SELECT_CALL_DISPOSITION')"
          call-disposition
          class="min-w-50"
        />
        <button
          v-tooltip="$t('DEALS.DEAL_NOTES.VISIBLE_IN_DEALS_LIST')"
          data-cy="visible-in-deals-list"
          class="flex h-9 w-10 cursor-pointer items-center justify-center rounded-md p-1 py-3"
          :class="{ 'bg-primary': visibleInDealsList }"
          @click.prevent.stop="toggleVisibleInDealsList"
        >
          <icon-base
            :icon="IconListView"
            height="20"
            width="20"
            view-box="0 0 24 24"
            class="mx-1 cursor-pointer"
            :class="visibleInDealsList ? 'text-white' : 'text-gray-400'"
          />
        </button>
      </div>
    </template>
  </log-quick-submit>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { COLORS_BY_DISPOSITION_NOTE_STATUS } from "@/helpers/constants/colors";
import { usePromiseWrapper } from "@/hooks/common";
import { useCommunicationLogs } from "@/hooks/communicationLogs";
import { useDeals } from "@/hooks/deals";
import communicationService from "@/services/modules/communication";
import { useCommunicationStore } from "@/stores/communication";

import LogQuickSubmit from "@/components/communicationLogs/forms/LogQuickSubmit.vue";
import IconListView from "@/components/icons/IconListView.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfTab from "@/components/ui/LfTab.vue";
import NotesDropdown from "@/components/ui/NotesDropdown.vue";
import LfSwitch from "@/components/ui/inputs/LfSwitch.vue";

import type { CallDisposition } from "@/enums/communicationLogs";
import type { CallLog, CallNotePayload } from "@/models/communicationLogs";

interface Props {
  callLog?: CallLog;
  isModal?: boolean;
}

interface TextType {
  name: string;
  value: string;
  isActive: boolean;
}

const { callLog, isModal } = defineProps<Props>();
const emit = defineEmits<{
  "callNote:edit": [];
  "callNote:create": [upload: boolean, note: CallLog];
}>();

const { getCommunicationList, updateCallNote, createCallNote } =
  useCommunicationStore();
const { getters } = useStore();
const { activeDeal } = useDeals();
const route = useRoute();
const { isInboxPage } = useCommunicationLogs();
const { t } = useI18n();

const callDisposition = ref(callLog?.data?.call_disposition);
const visibleInDealsList = ref(true);
const uploadAfterSave = ref(false);

const textTypes = ref<TextType[]>([
  {
    name: t("COMMON.NOTE"),
    value: callLog?.data?.body ?? "",
    isActive: true,
  },
  {
    name: t("COMMON.SUMMARY"),
    value: "",
    isActive: false,
  },
  {
    name: t("COMMON.TRANSCRIPTION"),
    value: "",
    isActive: false,
  },
]);

const callDispositions = computed<string[]>(
  () => getters["options/callDispositions"]
);

const activeTextType = computed(() =>
  textTypes.value.find((type) => type.isActive)
);

const callStatuses = computed(() =>
  Object.entries(callDispositions.value).map(([id, value]) => ({
    id,
    name: value,
  }))
);

const placeholder = computed(() =>
  activeTextType.value?.name === t("COMMON.NOTE")
    ? t("ACTIVITY.CONTACTS.WRITE_NOTES_ABOUT_THIS_CALL")
    : t("ACTIVITY.CONTACTS.WRITE_TEXT_ABOUT_THIS_CALL", {
        type: activeTextType.value?.name.toLowerCase(),
      })
);

const loading = computed(
  () =>
    isUpdatingNote.value || isAddingSummary.value || isAddingTranscription.value
);

const { loading: isUpdatingNote, fetchWrapper: updateNote } = usePromiseWrapper(
  async (payload: CallNotePayload) => {
    if (!callLog) {
      return;
    }
    await updateCallNote(callLog.data.id, payload);
    const filters = route.query;

    await getCommunicationList(filters, isInboxPage.value);
  }
);

const handleActiveTextTypeChange = (textType: TextType) => {
  textTypes.value.forEach((type) => {
    type.isActive = type.name === textType.name;
  });
};

const updateActiveTypeValue = (value: string) => {
  const activeTextType = textTypes.value.find(({ isActive }) => isActive);

  if (!activeTextType) {
    return;
  }

  activeTextType.value = value.trim();
};

const { fetchWrapper: createNote } = usePromiseWrapper(
  async (payload: CallNotePayload) => {
    const note = await createCallNote(activeDeal.value.id, payload);
    emit("callNote:create", uploadAfterSave.value, note);
  }
);

const toggleVisibleInDealsList = () => {
  visibleInDealsList.value = !visibleInDealsList.value;
};

const noteAction = async () => {
  const note =
    textTypes.value.find(({ name }) => name === t("COMMON.NOTE"))?.value ?? "";

  const payload = {
    note,
    call_disposition: callDisposition.value as CallDisposition,
    visible_in_deal_list: visibleInDealsList.value,
  };

  const action = callLog ? updateNote : createNote;
  await action(payload);
};

const { fetchWrapper: transcriptionAction, loading: isAddingTranscription } =
  usePromiseWrapper(async () => {
    const content = textTypes.value.find(
      ({ name }) => name === t("COMMON.TRANSCRIPTION")
    )?.value;

    if (!callLog?.data.id || !content) {
      return;
    }
    const payload = {
      content,
    };

    await communicationService.addCallLogTranscription(
      callLog.data.id.toString(),
      payload
    );
  });

const { fetchWrapper: summaryAction, loading: isAddingSummary } =
  usePromiseWrapper(async () => {
    const content = textTypes.value.find(
      ({ name }) => name === t("COMMON.SUMMARY")
    )?.value;

    if (!callLog?.data.id || !content) {
      return;
    }

    const payload = {
      content,
    };

    await communicationService.addCallLogSummary(
      callLog.data.id.toString(),
      payload
    );
  });

const onSubmit = async () => {
  const typeActionMapper = {
    [t("COMMON.TRANSCRIPTION")]: transcriptionAction,
    [t("COMMON.SUMMARY")]: summaryAction,
    [t("COMMON.NOTE")]: noteAction,
  };

  const actions = Object.keys(typeActionMapper).reduce<Promise<void>[]>(
    (res, key) => {
      const hasValue = textTypes.value.find(({ name }) => name === key)?.value;
      const action = typeActionMapper[key];

      if (!action || (!hasValue && key !== t("COMMON.NOTE"))) {
        return res;
      }

      res.push(action());
      return res;
    },
    []
  );

  await Promise.all(actions);
  emit("callNote:edit");
};
</script>
